import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../config/config.json';
import { Item } from '../../models/item';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

// Product interface based on the structure used in ProductShop
export interface Product {
  id: number;
  title: string;
  name?: string;
  description?: string;
  stats_description?: string;
  shop_product_link: string;
  fg_price: string;
  shop_price_factor?: number;
  product_image_path: string;
  skin_image_path: string;
  imagePath?: string;
  skinPath?: string;
  rarity_id: number;
  ladder: number;
  hardcore: number;
  status?: string;
  node_id?: number;
  shop_product_name?: string;
  search_equivalent?: string;
  search_term?: string;
  type_id?: number;
  ethereal?: number;
  unid?: number;
  fiat_only?: number;
  keep_limit?: number;
  early_ladder_price?: string;
  mid_ladder_price?: string;
  late_ladder_price?: string;
  last_update_user_id?: number;
  pickit_nip?: string;
  pickit_gid?: string;
  pickit_keke?: string;
  pickit_in?: string;
  stats?: Array<{ stat_description?: string }> | null;
  created_at?: string;
  updated_at?: string;
}

// Query key factory
export const searchKeys = {
  all: ['items', 'search'] as const,
  search: (term: string) => [...searchKeys.all, term] as const,
  products: ['products', 'search'] as const,
  productSearch: (term: string) => [...searchKeys.products, term] as const,
};

// Search function that performs the API call for items
const searchItems = async (
  searchTerm: string,
  errorHandler: (error: any) => void
): Promise<Item[]> => {
  const sessionToken = localStorage.getItem('session_token');

  try {
    const response = await axios.post(
      `${config.backend_base_url}public/pss/search`,
      { search_term: searchTerm },
      { headers: { 'session-token': sessionToken } }
    );

    return response.data || [];
  } catch (error) {
    console.error('Error searching items:', error);
    errorHandler(error);
    return [];
  }
};

// Search function that performs the API call for products
const searchProducts = async (
  searchTerm: string,
  errorHandler: (error: any) => void
): Promise<Product[]> => {
  const sessionToken = localStorage.getItem('session_token');

  try {
    const response = await axios.post(
      `${config.backend_base_url}ml/pickit/dynamic/psearch`,
      { search_term: searchTerm },
      { headers: { 'session-token': sessionToken } }
    );

    return response.data || [];
  } catch (error) {
    console.error('Error searching products:', error);
    errorHandler(error);
    return [];
  }
};

// Hook to search items
export const useSearchItems = (searchTerm: string, enabled = true, allowEmptySearch = false) => {
  const { t } = useTranslation();

  return useQuery({
    queryKey: searchKeys.search(searchTerm),
    queryFn: () =>
      searchItems(searchTerm, (_error) => {
        toast.error(t('search.errors.searchFailed', 'Failed to search items. Please try again'));
      }),
    enabled: enabled && (allowEmptySearch || searchTerm !== ''),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
    refetchOnWindowFocus: false,
  });
};

// Hook to search products
export const useSearchProducts = (searchTerm: string, enabled = true, allowEmptySearch = false) => {
  const { t } = useTranslation();

  return useQuery({
    queryKey: searchKeys.productSearch(searchTerm),
    queryFn: () =>
      searchProducts(searchTerm, (_error) => {
        toast.error(
          t('search.errors.productSearchFailed', 'Failed to search products. Please try again')
        );
      }),
    enabled: enabled && (allowEmptySearch || searchTerm !== ''),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
    refetchOnWindowFocus: false,
  });
};
